import Card from '@/components/Card';
import { Button } from '@/components/Button';
import { FormField } from '@/components/Form/FormField';
import { Switch } from '@/components/Controls/Switch';
import { Trans } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { useEffect } from 'react';
import { Controller, FieldValues, Path } from 'react-hook-form';
import { FormManagementProps } from '@/type/form.types';
import { RichTextEditor } from '@/components/Custom/RichTextEditor';
import { getTranslatedItem } from '@/utils/language.utils';
import { setGroupRequiredFields } from '@/utils/required-fields/group.utils';
import { TitleSection } from '@/components/Header/Section/TitleSection';
import { EmailReminderManagement } from '@/components/Common/EmailReminderManagement/Index';
import { useUserRole } from '@/hooks/auth.hooks';

export const GroupWhiteLabelInfosManagement = <TSchema extends FieldValues>({
  t,
  data,
  watch,
  control,
  register,
  setValue,
  // setError,
  errors,
  submitDisabled,
  onSubmit,
}: FormManagementProps<TSchema>) => {
  const { isAdmin } = useUserRole();

  const translatedGroup = getTranslatedItem(data?.translatedGroups);

  /**
   * === Set Value Watchers section ===
   */
  const automatedEmailingActiveValue =
    watch &&
    watch(
      'automatedEmailingActive' as Path<TSchema>,
      data?.automatedEmailingActive ?? false,
    );
  /**
   * === END OF Set Value Watchers section ===
   */
  /**
   * === Set Fields Initial Values section ===
   */
  useEffect(() => {
    if (setValue && data) {
      setGroupRequiredFields({ setValue, data });
      setValue(
        'automatedEmailingActive' as Path<TSchema>,
        data.automatedEmailingActive ?? false,
      );
      setValue(
        'legalNotice' as Path<TSchema>,
        (translatedGroup?.legalNotice || data.legalNotice) ?? '',
      );
      setValue(
        'privacyPolicy' as Path<TSchema>,
        (translatedGroup?.privacyPolicy || data.privacyPolicy) ?? '',
      );
      setValue(
        'cgu' as Path<TSchema>,
        (translatedGroup?.cgu || data.cgu) ?? '',
      );
      setValue(
        'appWarningMessage' as Path<TSchema>,
        (translatedGroup?.appWarningMessage || data.appWarningMessage) ?? '',
      );
      setValue(
        'partFolderNotFound' as Path<TSchema>,
        (translatedGroup?.partFolderNotFound || data.partFolderNotFound) ?? '',
      );
      setValue(
        'partContactUs' as Path<TSchema>,
        (translatedGroup?.partContactUs || data.partContactUs) ?? '',
      );
      setValue(
        'proContactUs' as Path<TSchema>,
        (translatedGroup?.proContactUs || data.proContactUs) ?? '',
      );
    }
  }, [setValue, data, translatedGroup]);
  /**
   * === END OF Set Fields Initial Values section ===
   */

  const getCardTitle = (title: string, iconClassName?: string) => {
    return (
      <TitleSection
        text={title}
        defaultIconClassName={twMerge('ki-notepad-bookmark', iconClassName)}
      />
    );
  };

  return (
    <>
      {isAdmin && (
        <>
          <form onSubmit={onSubmit}>
            <Card
              title={getCardTitle(
                t('automatedEmailingActivation'),
                'ki-setting',
              )}
              footer={
                <Button
                  buttonClassName="px-8"
                  text={
                    submitDisabled
                      ? `${t('common:loading')}...`
                      : t('common:save')
                  }
                  disabled={submitDisabled}
                />
              }
              cardClassName="mb-5"
              bodyClassName="py-8 flex flex-col gap-6"
            >
              <Switch
                rightText={t('activateGroupAutomatedEmailing')}
                rightTextClassName="text-slate-500 text-sm font-medium leading-tight"
                {...(register &&
                  register('automatedEmailingActive' as Path<TSchema>))}
                errorMessage={
                  errors && (errors.automatedEmailingActive?.message as string)
                }
                checked={automatedEmailingActiveValue}
              />
              <div className="text-sm text-gray-600">
                <Trans
                  i18nKey={t('configureGroupAutomatedEmailing')}
                  components={{
                    br: <br />,
                    a: (
                      <a
                        href="/email-settings"
                        target="_blank"
                        className="text-primary font-medium underline"
                      >
                        {t('common:here')}
                      </a>
                    ),
                  }}
                />
              </div>
            </Card>
          </form>
          <div className="mb-5">
            <EmailReminderManagement
              t={t}
              groupId={data?.id}
              contentContainerClassName="w-[74%]"
              templateLineClassName="max-[360px]:max-w-[70%]"
            />
          </div>
        </>
      )}
      <form onSubmit={onSubmit}>
        <Card
          title={getCardTitle(t('common:legalInformations'))}
          footer={
            <Button
              buttonClassName="px-8"
              text={
                submitDisabled ? `${t('common:loading')}...` : t('common:save')
              }
              disabled={submitDisabled}
            />
          }
          cardClassName="mb-5"
        >
          <FormField
            containerClassName="mb-5"
            text={t('common:legalMentions')}
            textWrapperClassName="mt-3"
          >
            <Controller
              name="legalNotice"
              control={control}
              render={({ field }) => (
                <RichTextEditor value={field.value} onChange={field.onChange} />
              )}
            />
          </FormField>
          <FormField
            containerClassName="mb-5"
            textWrapperClassName="mt-3"
            text={t('common:privacyPolicy')}
          >
            <Controller
              name="privacyPolicy"
              control={control}
              render={({ field }) => (
                <RichTextEditor value={field.value} onChange={field.onChange} />
              )}
            />
          </FormField>
          <FormField text={t('common:cgu')} textWrapperClassName="mt-3">
            <Controller
              name="cgu"
              control={control}
              render={({ field }) => (
                <RichTextEditor value={field.value} onChange={field.onChange} />
              )}
            />
          </FormField>
        </Card>
      </form>
      <form onSubmit={onSubmit}>
        <Card
          cardClassName="mb-5"
          title={getCardTitle(t('common:infoLinkedForPart'))}
          footer={
            <Button
              buttonClassName="px-8"
              text={
                submitDisabled ? `${t('common:loading')}...` : t('common:save')
              }
              disabled={submitDisabled}
            />
          }
        >
          <FormField
            containerClassName="mb-5"
            text={t('common:referenceNumberNotFound')}
            textWrapperClassName="mt-3"
          >
            <Controller
              name="partFolderNotFound"
              control={control}
              render={({ field }) => (
                <RichTextEditor value={field.value} onChange={field.onChange} />
              )}
            />
          </FormField>
          <FormField
            containerClassName="mb-5"
            text={t('common:contactUs')}
            textWrapperClassName="mt-3"
          >
            <Controller
              name="partContactUs"
              control={control}
              render={({ field }) => (
                <RichTextEditor value={field.value} onChange={field.onChange} />
              )}
            />
          </FormField>
        </Card>
      </form>
      <form onSubmit={onSubmit}>
        <Card
          cardClassName="mb-5"
          title={getCardTitle(t('common:infoLinkedForPro'))}
          footer={
            <Button
              buttonClassName="px-8"
              text={
                submitDisabled ? `${t('common:loading')}...` : t('common:save')
              }
              disabled={submitDisabled}
            />
          }
        >
          <FormField
            containerClassName="mb-5"
            text={t('common:contactUs')}
            textWrapperClassName="mt-3"
          >
            <Controller
              name="proContactUs"
              control={control}
              render={({ field }) => (
                <RichTextEditor value={field.value} onChange={field.onChange} />
              )}
            />
          </FormField>
        </Card>
      </form>
      <form onSubmit={onSubmit}>
        <Card
          title={getCardTitle(t('common:app'))}
          footer={
            <Button
              buttonClassName="px-8"
              text={
                submitDisabled ? `${t('common:loading')}...` : t('common:save')
              }
              disabled={submitDisabled}
            />
          }
        >
          <FormField
            containerClassName="mb-5"
            text={t('common:appWarningMessage')}
            textWrapperClassName="mt-3"
          >
            <Controller
              name="appWarningMessage"
              control={control}
              render={({ field }) => (
                <RichTextEditor value={field.value} onChange={field.onChange} />
              )}
            />
          </FormField>
        </Card>
      </form>
    </>
  );
};
